import { FormEvent, useEffect, useState } from "react";
import { toast } from "@/utils/appToast";
import { useTranslation } from 'react-i18next';

import { api } from "@/utils/api";
import ResetPassword from "@/pages/auth/resetPassword/index";
import { PhoneVerification } from "../phoneVerify";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { IconButton } from "@mui/material";
import { ArrowBackWhiteIcon } from "@/components/assets/icons/svgs";

const COOLDOWN_KEY = "emailResetCooldown";
const COOLDOWN_DURATION = 300; // 5 minutes


// Email validation function
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const ForgotPassword = ({ handlePopupType }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"email" | "phone">("email");
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [token, setToken] = useState("");

  // Restore cooldown from storage
  useEffect(() => {
    const stored = localStorage.getItem(COOLDOWN_KEY);
    if (stored) {
      const expiry = parseInt(stored, 10);
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiry - now;
      if (remaining > 0) {
        setCooldown(remaining);
        setEmailSent(true);
      } else {
        localStorage.removeItem(COOLDOWN_KEY);
      }
    }
  }, []);

  const restoreEmailCooldown = () => {
  const stored = localStorage.getItem(COOLDOWN_KEY);
  if (stored) {
    const expiry = parseInt(stored, 10);
    const now = Math.floor(Date.now() / 1000);
    const remaining = expiry - now;
    if (remaining > 0) {
      setCooldown(remaining);
      setEmailSent(true);
    } else {
      localStorage.removeItem(COOLDOWN_KEY);
    }
  }
};

useEffect(() => {
  if (activeTab === "email") {
    restoreEmailCooldown();
  }
}, [activeTab]);


  // Countdown logic
  useEffect(() => {
    if (!emailSent || cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          localStorage.removeItem(COOLDOWN_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [emailSent, cooldown]);

  useEffect(() => {
  setEmail("");
  setPhone("");
  setPhoneError("");
  setShowVerificationForm(false);
  setShowResetForm(false);

  if (activeTab === "phone") {
    // Reset only if user switches to phone
    setEmailSent(false);
    setCooldown(0);
  }

  if (activeTab === "email") {
    // Restore cooldown if returning to email
    const stored = localStorage.getItem(COOLDOWN_KEY);
    if (stored) {
      const expiry = parseInt(stored, 10);
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiry - now;
      if (remaining > 0) {
        setCooldown(remaining);
        setEmailSent(true);
      } else {
        localStorage.removeItem(COOLDOWN_KEY);
      }
    }
  }
}, [activeTab]);


  const updateToken = (token: string) => {
    setToken(token);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const value = formData.get(activeTab) as string;

      if (!value) {
        toast.error(`Please enter your ${activeTab === "email" ? "email" : "phone number"}`);
        return;
      }

      if (activeTab === "email") {
        const emailInput = formData.get("email") as string;
        setEmail(emailInput);

        if (!emailInput || !validateEmail(emailInput)) {
          toast.error('Please enter a valid email address');
          return;
        }

        const data = await api.forgotPassword({ type: "email", email: emailInput });
        toast.success("Se ha enviado un enlace de verificación por correo electrónico a su dirección de correo electrónico");
        setEmailSent(true);
        const expiry = Math.floor(Date.now() / 1000) + COOLDOWN_DURATION;
        localStorage.setItem(COOLDOWN_KEY, expiry.toString());
        setCooldown(COOLDOWN_DURATION);

      } else {
        // Validate phone number
        if (!phone || phone.replace(/\D/g, '').length < 11) {
          toast.error("Por favor ingrese un número de teléfono válido.");
          setPhoneError('Por favor ingrese un número de teléfono válido.');
          setIsLoading(false);
          return;
        }

        const fullPhone = '+' + phone; // react-phone-input-2 gives value without '+'
        const data = await api.forgotPassword({ type: "sms", phone: fullPhone });
        setPhone(fullPhone);
        setShowVerificationForm(true);
        toast.success("Se ha enviado un código de verificación a tu teléfono.");
      }

    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Unexpected error occurred. Please try again.";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetClose = () => {
    handlePopupType('Close');
  };

  const handlePhoneClose = () => {
    console.log("phone form closed");
  };

  const handleVerificationSuccess = (phone: string) => {
    setToken(phone);
    setPhoneCode(phone);
    setShowVerificationForm(false);
    setShowResetForm(true);
  };

  const handleResendEmail = async () => {
    try {
      await api.forgotPassword({ type: "email", email });
      toast.success("Correo reenviado correctamente");
      const expiry = Math.floor(Date.now() / 1000) + COOLDOWN_DURATION;
      localStorage.setItem(COOLDOWN_KEY, expiry.toString());
      setCooldown(COOLDOWN_DURATION);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Error al reenviar el correo";
      toast.error(`Error: ${message}`);
    }
  };

  return (
    <div className="login-form default-form">
      <div className="form-inner">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', marginTop: '20px' }}>
          <img src="/images/phone_symbol.svg" alt="Phone Symbol" style={{ width: '60px', height: '60px' }} />
        </div>
        <h3 className="text-center" style={{ color: "white", fontWeight: 600 }}>
          {showResetForm ? t('forgotPassword') :
            showVerificationForm ? "" :
              t('forgottenPassword')}
        </h3>
        {!showResetForm && !showVerificationForm ? (
          <>
            <div className="tabs-box">
              <div style={{ display: 'flex', marginBottom: '30px' }}>
                {["email", "phone"].map((tab) => (
                  <button
                    key={tab}
                    className={`theme-btn btn-style-one w-100 ${activeTab === tab ? "active" : ""}`}
                    onClick={() => setActiveTab(tab as "email" | "phone")}
                    style={{
                      backgroundColor: activeTab === tab ? "#2D74EA" : "#5997FF",
                      border: activeTab === tab ? "1px solid #fff" : "transparent",
                      color: "white",
                      height: '45px',
                      fontSize: '16px',
                      borderRadius: 0
                    }}
                  >
                    {tab === "email" ? t('byEmail') : t('byPhone')}
                  </button>
                ))}
              </div>
              <form onSubmit={handleSubmit} className="default-form">
                <div className="form-group">
                  <label className="text-white">
                    {activeTab === "email" ? t('email') : t('phone')} <span style={{ color: "red" }}> *</span>
                  </label>
                  {activeTab === "phone" ? (
                    <div className="form-group">
                      <PhoneInput
                        country={'us'}
                        countryCodeEditable={false}
                        value={phone}
                        onChange={value => {
                          setPhone(value);
                          setPhoneError('');
                        }}
                        inputProps={{
                          name: 'phone',
                          required: true,
                          style: { width: '100%', minHeight: '50px', fontSize: '16px' }
                        }}
                        inputStyle={{
                          width: '100%',
                          borderRadius: 0,
                          minHeight: '50px',
                          fontSize: '16px'
                        }}
                        buttonStyle={{
                          borderRadius: 0
                        }}
                        containerStyle={{
                          width: '100%'
                        }}
                        specialLabel=""
                        disableDropdown={false}
                        onlyCountries={['us']}
                        onBlur={() => {
                          if (phone && phone.replace(/\D/g, '').length < 11) setPhoneError('Por favor ingrese un número de teléfono válido.');
                        }}
                      />
                      {/* {phoneError && <div style={{ color: 'red', fontSize: 12 }}>{phoneError}</div>} */}
                    </div>
                  ) : (
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-control"
                      placeholder={t('enterEmail')}
                      required
                      disabled={emailSent}
                      style={{ borderRadius: 0, height: '50px', fontSize: '16px', padding: '10px 20px' }}
                    />
                  )}
                </div>
                {!emailSent && (
                  <div className="form-group">
                    <div className="flex justify-between items-center">
                      <IconButton className="min-w-[50px]" onClick={() => handlePopupType('login')}>
                        <ArrowBackWhiteIcon />
                      </IconButton>
                    <button
                      className="theme-btn btn-style-one w-100"
                      type="submit"
                      disabled={isLoading}
                      style={{ backgroundColor: "#1967d2", border: "none", padding: '14px 0px', fontSize: '16px', borderRadius: 14 }}
                    >
                      {isLoading ? (
                        <div className="spinner-border spinner-border-sm" role="status"></div>
                      ) : (
                        t('continue')
                      )}
                    </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
            {emailSent && activeTab === "email" && (
             <div className="flex  items-center">
             <IconButton className="min-w-[50px]" onClick={() => handlePopupType('login')}>
               <ArrowBackWhiteIcon />
             </IconButton>
              <div className="text-center text-white mt-3 w-full">
                <p className="text-white opacity-75">{t('didNotReceiveEmail')}</p>
                <button
                  onClick={handleResendEmail}
                  disabled={cooldown > 0}
                  className="btn btn-link text-decoration-underline"
                  style={{
                    color: "#fff",
                    fontSize: "16px",
                    padding: 0,
                    textDecoration: "underline",
                    opacity: cooldown > 0 ? 0.5 : 1,
                    cursor: cooldown > 0 ? "not-allowed" : "pointer"
                  }}
                >
                  {cooldown > 0
                    ? `${t('retryIn')} ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
                    : t('tryAgain')}
                </button>
              </div>
              </div>
            )}
          </>
        ) : showResetForm ? (
          <ResetPassword email={email} onHide={handleResetClose} token={token} onGoToLogin={() => handlePopupType("Close")} />
        ) : (
          <PhoneVerification phone={phone} onHide={handlePhoneClose} onVerificationSuccess={handleVerificationSuccess} />
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
